import React, { useState, type ComponentType } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import Heading from '@atlaskit/heading/heading';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import type { IconTileProps, NewCoreIconProps } from '@atlaskit/icon/types';
import IconTile from '@atlaskit/icon/icon-tile';
import Tile from '@atlaskit/tile/tile';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';

import { DismissComponent } from './DismissComponent.tsx';

const styles = cssMap({
	iconContainer: {
		gap: token('space.200'),
	},
	titleContainer: {
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	},
	popupBox: {
		width: '360px',
		margin: token('space.200'),
	},
	popupDescription: {
		marginTop: token('space.100'),
		marginBottom: token('space.200'),
	},
	textContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
	},
	ctaStyles: {
		color: token('color.link'),
		backgroundColor: 'transparent',
		padding: token('space.0'),
		margin: token('space.0'),
		textDecoration: 'underline',
		font: token('font.body'),
		'&:hover': {
			textDecoration: 'none',
		},
	},
	showMoreIcon: {
		backgroundColor: token('color.background.neutral.subtle'),
	},
});

export interface PromptContentProps {
	titleText: { id: string; defaultMessage: string; description: string };
	descriptionText: { id: string; defaultMessage: string; description: string };
	ctaText: { id: string; defaultMessage: string; description: string };
	dismissText: { id: string; defaultMessage: string; description: string };
	contentIcon: ComponentType<NewCoreIconProps>;
	iconColor: IconTileProps['appearance'];
	iconAltText: { id: string; defaultMessage: string; description: string };
	onClickAction: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	contentType: string;
	onDismiss: () => void;
	competitorName: string;
	linkType?: string;
	editorAppearance?: string;
	pageAnalyticsAttributes?: any;
}

export const PromptContent: React.FC<PromptContentProps> = ({
	titleText,
	descriptionText,
	ctaText,
	dismissText,
	contentIcon: ContentIcon,
	iconColor,
	iconAltText,
	onClickAction,
	contentType,
	onDismiss,
	competitorName,
	linkType,
	editorAppearance,
	pageAnalyticsAttributes,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isDismissMenuOpen, setIsDismissMenuOpen] = useState(false);

	const handleMoreMenuClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'competitorLinkPrompt',
				actionSubjectId: 'promptMoreMenu',
				source: 'competitorLink',
				attributes: {
					linkContentType: contentType,
					linkType,
					editorAppearance,
					...pageAnalyticsAttributes,
				},
			},
		}).fire();

		setIsDismissMenuOpen(!isDismissMenuOpen);
	};

	const handleDismissClose = () => {
		setIsDismissMenuOpen(false);
	};

	const dismissPopup = (
		<Popup
			shouldRenderToParent={false}
			isOpen={isDismissMenuOpen}
			onClose={handleDismissClose}
			placement="bottom-start"
			content={() => (
				<DismissComponent
					dismissMessage={dismissText}
					onDismiss={() => {
						onDismiss();
						handleDismissClose();
					}}
				/>
			)}
			trigger={(triggerProps) => (
				<Pressable
					{...triggerProps}
					xcss={styles.showMoreIcon}
					onClick={handleMoreMenuClick}
					testId="competitor-link-prompt-dismiss-menu"
				>
					<ShowMoreHorizontalIcon label={intl.formatMessage(dismissText)} size="medium" />
				</Pressable>
			)}
		/>
	);

	const titleSection = (
		<Flex xcss={styles.titleContainer}>
			<Heading size="xsmall">{intl.formatMessage(titleText)}</Heading>
			{dismissPopup}
		</Flex>
	);

	const contentSection = (
		<Box>
			<Box xcss={styles.popupDescription}>
				<Text>{intl.formatMessage(descriptionText, { competitorName })}</Text>
			</Box>
			<Pressable onClick={onClickAction} xcss={styles.ctaStyles}>
				{intl.formatMessage(ctaText, { competitorName })}
			</Pressable>
		</Box>
	);

	return (
		<Box xcss={styles.popupBox} testId="competitor-link-prompt-popup">
			<Flex xcss={styles.iconContainer}>
				{/* eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred. */}
				{fg('confluence_frontend_tile_object_system_migration') ? (
					<Tile
						key={`${contentType}Icon`}
						testId={`competitor-link-prompt-content-tile-${contentType}`}
						label={intl.formatMessage(iconAltText)}
						// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Tile expects a design token name.
						backgroundColor="color.background.accent.teal.subtle"
						size="small"
					>
						<ContentIcon size="small" label="" />
					</Tile>
				) : (
					<IconTile
						key={`${contentType}Icon`}
						icon={ContentIcon}
						label={intl.formatMessage(iconAltText)}
						appearance={iconColor}
						size="small"
					/>
				)}
				<Box xcss={styles.textContainer}>
					{titleSection}
					{contentSection}
				</Box>
			</Flex>
		</Box>
	);
};
